import React, { useEffect, useRef, useState } from "react";
import {
  WEEKDAYS,
  addMonths,
  allowed,
  clamp,
  dayLabel,
  monthEnd,
  monthGrid,
  monthStart,
  monthTitle,
  stepDay,
  todayYmd,
} from "./calendar.mjs";

/*
 * One or more month grids side by side, with ‹ › to page. Picks a single day
 * (`value`) or shows a range (`from`, `to`, and `hover` while the second end is
 * still being chosen). Arrow keys walk the days, PageUp/PageDown the months,
 * Home/End the week; the grid pages itself when focus walks off its edge.
 */
export default function Months({ count = 1, view, onView, value, from, to, hover, min, max, onPick, onHover, autoFocus = true }) {
  const today = todayYmd();
  const [focus, setFocus] = useState(() => clamp(value || to || from || today, min, max));
  const root = useRef(null);
  const keyed = useRef(autoFocus);

  useEffect(() => {
    if (!keyed.current || !root.current) return;
    keyed.current = false;
    const btn = root.current.querySelector(`[data-day="${focus}"]`);
    if (btn) btn.focus();
  }, [focus, view]);

  const last = monthEnd(addMonths(view, count - 1));
  const visible = focus >= view && focus <= last;
  // The day that takes Tab when the focused one has paged out of sight.
  const tabDay = visible ? focus : clamp(view < today && today <= last ? today : view, min, max);

  const onKeyDown = (e) => {
    const from0 = e.target.dataset && e.target.dataset.day;
    if (!from0) return;
    const next = stepDay(from0, e.key);
    if (!next) return;
    e.preventDefault();
    const day = clamp(next, min, max);
    keyed.current = true;
    setFocus(day);
    if (day < view) onView(monthStart(day));
    else if (day > last) onView(monthStart(addMonths(day, -(count - 1))));
    if (onHover) onHover(day);
  };

  // The range as drawn: the chosen ends, or the first end and wherever the
  // pointer is while the second is still open.
  const end = from && !to ? hover : to;
  const lo = from && end && end < from ? end : from;
  const hi = from && end ? (end < from ? from : end) : null;

  return (
    <div className="dp-months" ref={root} onKeyDown={onKeyDown} onMouseLeave={() => onHover && onHover(null)}>
      {Array.from({ length: count }, (_, i) => {
        const month = addMonths(view, i);
        return (
          <div className="dp-month" key={month}>
            <div className="dp-month__head">
              {i === 0 ? (
                <button type="button" className="dp-nav" aria-label="Previous month" disabled={!!min && view <= min} onClick={() => onView(addMonths(view, -1))}>
                  <Chevron dir="left" />
                </button>
              ) : (
                <span className="dp-nav dp-nav--blank" />
              )}
              <span className="dp-month__title" aria-live="polite">{monthTitle(month)}</span>
              {i === count - 1 ? (
                <button type="button" className="dp-nav" aria-label="Next month" disabled={!!max && monthEnd(month) >= max} onClick={() => onView(addMonths(view, 1))}>
                  <Chevron dir="right" />
                </button>
              ) : (
                <span className="dp-nav dp-nav--blank" />
              )}
            </div>
            <div className="dp-grid" role="grid" aria-label={monthTitle(month)}>
              <div className="dp-row" role="row">
                {WEEKDAYS.map((w) => (
                  <span key={w} className="dp-dow" role="columnheader">{w}</span>
                ))}
              </div>
              {monthGrid(month).map((week) => (
                <div className="dp-row" role="row" key={week[0].day}>
                  {week.map(({ day, inMonth }) => {
                    // Side by side, a day outside its month would show twice.
                    if (!inMonth && count > 1) return <span key={day} className="dp-cell" role="gridcell" />;
                    const ok = allowed(day, min, max);
                    const isOn = value === day || (!!lo && (day === lo || day === hi));
                    const band = !!hi && day > lo && day < hi;
                    const cls = [
                      "dp-cell",
                      band && "is-band",
                      hi && hi !== lo && day === lo && "is-lo",
                      hi && hi !== lo && day === hi && "is-hi",
                    ].filter(Boolean).join(" ");
                    return (
                      <span key={day} className={cls} role="gridcell" aria-selected={isOn || band}>
                        <button
                          type="button"
                          data-day={day}
                          className={[
                            "dp-day",
                            !inMonth && "is-out",
                            isOn && "is-on",
                            day === today && "is-today",
                          ].filter(Boolean).join(" ")}
                          tabIndex={day === tabDay ? 0 : -1}
                          disabled={!ok}
                          aria-label={`${dayLabel(day, { now: new Date(0) })}${day === today ? ", today" : ""}`}
                          aria-pressed={isOn}
                          onClick={() => {
                            setFocus(day);
                            onPick(day);
                          }}
                          onMouseEnter={() => onHover && ok && onHover(day)}
                          onFocus={() => setFocus(day)}
                        >
                          {+day.slice(8)}
                        </button>
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Chevron({ dir }) {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={dir === "left" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
    </svg>
  );
}

export function CalendarIcon({ size = 16 }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v4M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </svg>
  );
}
